/** Thrown when the server rejects an action payload against its Zod schema. */
export const VALIDATION_ERROR_CODE = "VALIDATION_ERROR";

export type ValidationErrors = Record<string, Array<string>>;

/**
 * Thrown when the server returns `{ type: "redirect" }` on `/action`.
 * Usually handled by {@link applyActionRedirect}; re-thrown when `external`
 * is false and the caller should SPA-navigate instead.
 */
export class TinkertoyActionRedirectError extends Error {
  readonly location: string;
  readonly external: boolean;

  constructor(location: string, options?: { external?: boolean }) {
    super(`Redirect to ${location}`);
    this.name = "TinkertoyActionRedirectError";
    this.location = location;
    this.external = options?.external ?? true;
  }

  static isInstance(err: unknown): err is TinkertoyActionRedirectError {
    return err instanceof TinkertoyActionRedirectError;
  }
}

/**
 * Structured action failure the shell surfaces to modules.
 *
 * Matches the spirit of `docs/tinkertoy-v2-design.md` (ActionError / validation_error).
 */
export class TinkertoyActionError extends Error {
  readonly code: string | undefined;
  readonly validationErrors: ValidationErrors | undefined;

  constructor(
    message: string,
    options?: {
      code?: string;
      validationErrors?: ValidationErrors;
    },
  ) {
    super(message);
    this.name = "TinkertoyActionError";
    this.validationErrors = options?.validationErrors;
    this.code =
      options?.code ??
      (options?.validationErrors ? VALIDATION_ERROR_CODE : undefined);
  }

  static isInstance(err: unknown): err is TinkertoyActionError {
    return err instanceof TinkertoyActionError;
  }
}
